import { Box, Checkbox, Dialog, Grid, MenuItem, TextField, Typography, FormControl } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import DescriptionIcon from '@material-ui/icons/Description';
import EmojiEmotionsIcon from '@material-ui/icons/EmojiEmotions';
import MobileOffIcon from '@material-ui/icons/MobileOff';
import { TraxxButton, TraxxInput, SearchSelect } from 'app/components';
import { GlobalRegex } from 'app/constants';
import { API } from 'app/services';
import { useAsyncTask } from 'app/utils';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    width: 500,
    [theme.breakpoints.down('xs')]: {
      width: 300,
    },
  },
  email: {
    marginTop: theme.spacing(2),
  },
  container: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
  },
  subtitle: {
    marginTop: theme.spacing(2),
  },
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    textAlign: 'center',
  },
  baseContainer: {
    justifyContent: 'flex-end',
  },
  buttonContainer: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  smallFont: {
    fontSize: 10,
  },
  icon: {
    marginRight: theme.spacing(2),
  },
}));

const InviteDialog = (props) => {
  const { onClose, open } = props;
  const classes = useStyles();
  const [required, setRequired] = useState({
    selfie: true,
    document: true,
    live_document: true,
    force_selfie: true,
    berbix_type: true,
    idType: 'All',
  });
  const [runLoadCase] = useAsyncTask('loadCase');
  const [caseOptions, setCaseOptions] = useState([]);
  const [caseValue, setCaseValue] = useState('');
  const [timer, setTimer] = useState(null);
  const [toggle, setToggle] = useState('regtank');

  const [errors, setErrors] = useState({});

  const [runSendInvitation, sendInviteLoading] = useAsyncTask('sendInvitation');
  const loadTask = useSelector((state) => state.layout.loadingTasks);
  const workspace = useSelector((state) => state.profile.workspace);

  const [inputs, setInputs] = useState({
    name: '',
    email: '',
    first_name: '',
    last_name: '',
    contacts: [],
    verification_type: null,
  });
  const [loading, setLoading] = useState();
  const { enqueueSnackbar } = useSnackbar();
  const access = useSelector((state) => state.profile.access);
  const [redoDocument, setRedoDocument] = useState(false);
  
  useEffect(() => {
    if (inputs.verification_type === 'upload') {
      setRequired((prev) => ({
        ...prev,
        document: false, // ✅ default false for upload
      }));
    } else {
      setRequired((prev) => ({
        ...prev,
        document: true, // ✅ reset back for other types
      }));
    }
  }, [inputs.verification_type]);
  const handleClose = () => {
    setInputs({ name: '', email: '', first_name: '', last_name: '', contacts: [], verification_type: null });
    setErrors({});
    setToggle('regtank');
    setRequired({
      selfie: true,
      document: true,
      live_document: true,
      force_selfie: true,
      berbix_type: true,
      idType: 'All',
    });
    onClose();
  };

  useEffect(() => {
    if (access && access.token) {
      loadCases();
    }
    // eslint-disable-next-line
  }, [access]);

  const privileges = workspace?.account?.entity?.privileges || [];
  const hasSumsub = privileges.some((p) => p.name === 'sumsub_identification');
  const hasRegtank = privileges.some((p) => p.name === 'regtank_identification');
  const canSend = (toggle === 'sumsub' && hasSumsub) || (toggle === 'regtank' && hasRegtank);

  const handleIdTypeChange = (type) => {
    setRequired((prev) => ({ ...prev, idType: type }));
  };

  const loadCases = (search) => {
    runLoadCase(async () => {
      let filter = { limit: 1000, offset: 0 };
      if (search) filter.search = search;
      const result = await API.Screening.list_case(filter);

      const caseOpts = result.models.map((mod) => {
        const contacts = (mod.verifications || [])
          .filter((v) => v.verification_request)
          .map((v) => ({
            name: v.verification_request.target_name,
            email: v.verification_request.target_email,
            type: v.type, // liveness or upload
          }))
          .filter((contact, index, self) => index === self.findIndex((c) => c.email === contact.email));

        return {
          name: `${mod.reference}${mod.label ? ` - ${mod.label}` : ''}${mod.status ? ` (${mod.status})` : ''}`,
          value: mod.id,
          ref: mod.reference,
          contacts,
        };
      });

      if (search) {
        caseOpts.forEach((cas) => {
          if (cas.ref === search) setCaseValue(cas.name);
        });
      }

      setCaseOptions(caseOpts);
      setLoading(false);
    });
  };

  const onCaseInputs = (event, value, reason) => {
    setCaseValue(value);
    if (timer) clearTimeout(timer);
    setLoading(true);
    setTimer(setTimeout(() => loadCases(value), 1000));
  };

  const onCaseSelect = (event, value, reason) => {
    if (value && caseOptions) {
      let foundCase = caseOptions.find((c) => c.name === value);
      setInputs({ ...inputs, case_id: foundCase.value, contacts: foundCase.contacts });
    } else {
      setInputs({ ...inputs, case_id: null, contacts: [] });
      setCaseValue('');
    }
  };
  const estimatedCost = (() => {
    if (inputs.verification_type === 'upload') {
      return redoDocument ? 6 : 4;
    }

    return 6;
  })();
  const sendInvitation = () => {
    const type = toggle;
    if (loadTask.sendInvitation) return;

    if (inputs.email === '' || (inputs.name === '' && inputs.first_name === '' && inputs.last_name === '')) {
      setErrors({});
      return enqueueSnackbar('Please fill in the recipient details', { variant: 'error', autoHideDuration: 3000 });
    }

    let err = {};
    if (!inputs.email) {
      err.email = 'Email field cannot be empty';
      setErrors(err);
    }

    if (!inputs.name && (inputs.first_name || !inputs.last_name)) {
      err.name = 'Name field cannot be empty';
      setErrors(err);
    }

    if (!GlobalRegex.EmailValidation.test(inputs.email)) {
      err.email = 'Please enter a valid email';
      setErrors(err);
    }

    const { selfie, force_selfie, document, live_document, hide_document, berbix_type, idType } = required;
    const name = inputs.first_name ? inputs.first_name + ',' + inputs.last_name : inputs.name;

    let submitChecks = {
      liveness_enabled: true,
      facematch_enabled: true,
      doc_required: 'optional',
      doc_force_live: 'optional',
      selfie_required: 'optional',
      selfie_force_live: 'optional',
      berbix_type: true,
      target_email: inputs.email,
      target_name: name,
      id: idType,
      verification_type: inputs.verification_type,
    };

    if (inputs.case_id) submitChecks.case_id = inputs.case_id;

    if (document) {
      submitChecks.doc_required = 'required';
    } else {
      if (hide_document) submitChecks.doc_required = 'hidden';
      else submitChecks.doc_required = 'optional';
    }

    if (live_document) {
      submitChecks.doc_force_live = 'force_live';
    } else {
      if (hide_document) submitChecks.doc_force_live = 'no_live';
      else submitChecks.doc_force_live = 'optional';
    }

    if (selfie) {
      submitChecks.selfie_required = 'required';
      submitChecks.selfie_force_live = 'force_live';
    } else {
      if (force_selfie) {
        submitChecks.selfie_required = 'hidden';
        submitChecks.selfie_force_live = 'no_live';
      } else {
        submitChecks.selfie_required = 'optional';
        submitChecks.selfie_force_live = 'optional';
      }
    }

    berbix_type ? (submitChecks.berbix_type = 'passport_only') : (submitChecks.berbix_type = 'any_document');

    if (hide_document && force_selfie) {
      setErrors({});
      return enqueueSnackbar('Need to upload either selfie or document or both', { variant: 'error', autoHideDuration: 3000 });
    }

    if (type === 'sumsub') {
      runSendInvitation(async () => {
        await API.Users.send_sumsub_verification_invites(submitChecks);
        enqueueSnackbar('Invitation sent', { variant: 'success', autoHideDuration: 3000 });
        handleClose();
      });
    } else {
      runSendInvitation(async () => {
        await API.Users.send_regtank_verification_invites(submitChecks);
        enqueueSnackbar('Invitation sent', { variant: 'success', autoHideDuration: 3000 });
        handleClose();
      });
    }
  };

  return (
    <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={open}>
      <Box className={classes.root}>
        <Typography variant="h5">Send screening invitation</Typography>

        <Grid container spacing={1}>
          <Grid item xs={12} md={12}>
            <TraxxInput
              InputLabelProps={{ shrink: true }}
              label="Recipient email"
              placeholder="john.smith@example.com"
              onChange={({ target }) => setInputs({ ...inputs, email: target.value })}
              className={classes.email}
              error={errors.email}
              value={inputs.email}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {toggle === 'regtank' ? (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <TraxxInput
                    InputLabelProps={{ shrink: true }}
                    label="First name"
                    placeholder="John"
                    value={inputs.first_name}
                    className={classes.email}
                    error={errors.first_name}
                    onChange={({ target }) => setInputs({ ...inputs, first_name: target.value })}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TraxxInput
                    InputLabelProps={{ shrink: true }}
                    label="Last name"
                    placeholder="Smith"
                    className={classes.email}
                    value={inputs.last_name}
                    error={errors.last_name}
                    onChange={({ target }) => setInputs({ ...inputs, last_name: target.value })}
                  />
                </Grid>
              </Grid>
            ) : (
              <TraxxInput
                InputLabelProps={{ shrink: true }}
                label="Recipient name (email addressee)"
                placeholder="John Smith"
                className={classes.email}
                value={inputs.name}
                error={errors.name}
                onChange={({ target }) => setInputs({ ...inputs, name: target.value })}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth className={classes.email}>
              <SearchSelect label={'Select case'} inputValue={caseValue} options={caseOptions} loading={loading || loadTask.loadCase} onSelect={onCaseSelect} onInput={onCaseInputs} />
            </FormControl>
          </Grid>

          {/* Auto-fill from contacts */}
          {toggle === 'regtank' && inputs.contacts?.length > 0 && (
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Auto-fill from previous screening"
                defaultValue=""
                className={classes.email}
                onChange={(e) => {
                  const contact = inputs.contacts.find((c) => c.email === e.target.value);
                  if (!contact) return;
                  const parts = contact.name.includes(',') ? contact.name.split(',') : contact.name.split(' ');
                  setInputs({
                    ...inputs,
                    first_name: parts[0]?.trim() || '',
                    last_name: parts[1]?.trim() || '',
                    email: contact.email,
                    verification_type: contact.type,
                  });
                }}>
                <MenuItem value="" disabled>
                  Select a contact
                </MenuItem>
                {inputs.contacts.map((c, i) => (
                  <MenuItem key={i} value={c.email}>
                    {c.name} ({c.email})
                  </MenuItem>
                ))}
              </TextField>

              {inputs.verification_type && (
                <Box
                  mt={1}
                  p={1}
                  style={{
                    backgroundColor: inputs.verification_type === 'upload' ? '#fff8e1' : '#e8f5e9',
                    borderRadius: 4,
                  }}>
                  <Typography
                    variant="body2"
                    style={{
                      color: inputs.verification_type === 'upload' ? '#f57f17' : '#2e7d32',
                    }}>
                    {inputs.verification_type === 'upload' ? '⚠️ Document uploaded — liveness test required' : '🔁 Liveness test already done — this will be a redo'}
                  </Typography>

                  {/* ✅ ADD THIS PART */}
                  {inputs.verification_type === 'upload' && (
                    <Box mt={1} display="flex" alignItems="center">
                      <Checkbox
                        checked={redoDocument}
                        onChange={(e) => {
                          const checked = e.target.checked;

                          setRedoDocument(checked);

                          setRequired((prev) => ({
                            ...prev,
                            document: checked ? true : prev.document, // force true when checked
                          }));
                        }}
                        size="small"
                      />
                      <Typography variant="body2">Redo document upload</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Grid>
          )}
        </Grid>

        {toggle === 'sumsub' && (
          <>
            <Typography className={classes.subtitle} variant="body1">
              Verification options
            </Typography>
            <Box className={classes.container}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <DescriptionIcon className={classes.icon} />
                  <Typography>ID/Passport</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={!!required.document} disabled onClick={() => setRequired({ ...required, document: !required.document })} />
                </Grid>
              </Grid>

              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <EmojiEmotionsIcon className={classes.icon} />
                  <Typography>Selfie</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={required.selfie} disabled onChange={() => setRequired({ ...required, selfie: !required.selfie })} />
                </Grid>
              </Grid>
            </Box>

            <Typography className={classes.subtitle} variant="body1">
              Additional requirements
            </Typography>
            <Box className={classes.container}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <CameraAltIcon className={classes.icon} />
                  <Typography>Force live capture (disable file upload)</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={!!required.live_document} disabled onClick={() => setRequired({ ...required, live_document: !required.live_document, document: !required.live_document && !required.document ? true : required.document })} />
                </Grid>
              </Grid>

              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <MobileOffIcon className={classes.icon} />
                  <Typography>Selfie mandatory</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={required.force_selfie && required.selfie} disabled onChange={() => setRequired({ ...required, force_selfie: !required.force_selfie })} />
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        {toggle === 'regtank' && (
          <>
            <Typography className={classes.subtitle} variant="body1">
              Verification options
            </Typography>
            <Box className={classes.container}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <DescriptionIcon className={classes.icon} />
                  <Typography>Passport</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={required.idType === 'PASSPORT'} onChange={() => handleIdTypeChange('PASSPORT')} />
                </Grid>
              </Grid>

              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <DescriptionIcon className={classes.icon} />
                  <Typography>Driving License</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={required.idType === 'DRIVING_LICENSE'} onChange={() => handleIdTypeChange('DRIVING_LICENSE')} />
                </Grid>
              </Grid>

              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <DescriptionIcon className={classes.icon} />
                  <Typography>Identity</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={required.idType === 'IDENTITY'} onChange={() => handleIdTypeChange('IDENTITY')} />
                </Grid>
              </Grid>

              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <DescriptionIcon className={classes.icon} />
                  <Typography>Residence Permit</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={required.idType === 'RESIDENCE_PERMIT'} onChange={() => handleIdTypeChange('RESIDENCE_PERMIT')} />
                </Grid>
              </Grid>

              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.itemContainer}>
                  <DescriptionIcon className={classes.icon} />
                  <Typography>All</Typography>
                </Grid>
                <Grid item>
                  <Checkbox checked={required.idType === 'All'} onChange={() => handleIdTypeChange('All')} />
                </Grid>
              </Grid>
            </Box>
          </>
        )}

        <Box mt={2}>
          <Typography align="right">Estimated cost: {estimatedCost} TXC</Typography>
        </Box>

        <ToggleButtonGroup value={toggle} exclusive onChange={(_, value) => value && setToggle(value)} size="small">
          <ToggleButton value="sumsub">Sumsub</ToggleButton>
          <ToggleButton value="regtank">Regtank</ToggleButton>
        </ToggleButtonGroup>

        {toggle === 'sumsub' && !hasSumsub && (
          <Typography color="error" variant="body2">
            You do not have access to the Sumsub integration. Please contact your administrator.
          </Typography>
        )}

        {toggle === 'regtank' && !hasRegtank && (
          <Typography color="error" variant="body2">
            You do not have access to the Regtank integration. Please contact your administrator.
          </Typography>
        )}

        <Box className={classes.baseContainer}>
          <Box className={classes.buttonContainer}>
            <TraxxButton color="default" onSubmit={() => handleClose()}>
              Cancel
            </TraxxButton>
            <TraxxButton
              loading={sendInviteLoading}
              disabled={!canSend}
              onSubmit={async () => {
                await sendInvitation();
                handleClose();
              }}>
              Send
            </TraxxButton>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default InviteDialog;
